import { Component, Input, Output, EventEmitter, ElementRef, OnInit, OnDestroy, OnChanges, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

import {
  CardapioItemPedidoDTO,
  CardapioEstabelecimentoDTO,
  CardapioFreteDTO,
  CardapioCheckoutFormDTO,
  CardapioExigenciasDoPedidoDTO,
  CardapioFormasDePagamentoDTO,
  CardapioEstadoCobrancaQrDTO,
} from '../../../../models/cardapio-digital/cardapio-digital.model';
import { TipoPedido } from '../../../../enums/TipoPedido.enum';
import { CardapioFreteService } from '../../../../services/cardapio-digital/cardapio-frete.service';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';

@Component({
  selector: 'app-cardapio-checkout',
  templateUrl: './cardapio-checkout.component.html',
  styleUrls: ['./cardapio-checkout.component.scss'],
  providers: [MessageService],
})
export class CardapioCheckoutComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() itens: CardapioItemPedidoDTO[] = [];
  @Input() estabelecimento!: CardapioEstabelecimentoDTO;
  @Input() freteConfig: CardapioFreteDTO | null = null;

  /**
   * Formas de pagamento que a loja aceita, vindas de GET /cardapio/{slug}.
   * Quando ausente ou com `exibir: false`, o checkout mantem o comportamento
   * historico (Dinheiro e Cartao) para nao quebrar lojas que nunca configuraram.
   */
  @Input() formasDePagamentoConfig: CardapioFormasDePagamentoDTO | null = null;
  /** O que a loja exige no consumo no local (mesa e/ou comanda). */
  @Input() exigenciasDoPedido: CardapioExigenciasDoPedidoDTO | null = null;
  /** Frete por distância (backend). Quando definido, tem prioridade sobre o
   *  valor fixo do `freteConfig` — assim o preview bate com a cobrança. */
  @Input() freteDistancia: number | null = null;
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Input() tipoPedidoInicial: TipoPedido | null = null;
  @Input() nomeClienteInicial = '';
  @Input() telefoneInicial = '';
  @Input() enderecoInicial = '';
  @Input() numeroMesaInicial = '';
  @Input() numeroComandaInicial = '';
  @Input() rucInicial = '';
  @Input() ciInicial = '';
  /**
   * Cobranca por QR do pedido, controlada pelo componente PAI — que e quem chama a API e faz o
   * polling. Mesmo desenho do `forma-pagamento-dialog` da tela de vendas.
   */
  @Input() estadoCobrancaQr: CardapioEstadoCobrancaQrDTO = { gerando: false, confirmado: false };
  @Output() voltar = new EventEmitter<void>();
  @Output() pedidoFinalizado = new EventEmitter<CardapioCheckoutFormDTO>();
  /**
   * Pede ao PAI a cobranca por QR assim que o cliente escolhe QR Code ou cartao.
   *
   * O codigo aparece aqui mesmo, no checkout: o cliente le, paga, e so entao o pedido e criado —
   * pelo callback da Bancard, nao por esta tela.
   */
  @Output() gerarCobrancaQr = new EventEmitter<CardapioCheckoutFormDTO>();
  /** Avisa o pai que a forma escolhida deixou de ser cobrada por QR (o cliente trocou de opcao). */
  @Output() cancelarCobrancaQr = new EventEmitter<void>();

  /** Bloco da cobranca (QR ou link do cartao) — alvo do scroll automatico. */
  @ViewChild('blocoCobrancaQr') blocoCobrancaQr?: ElementRef<HTMLElement>;

  // Formulario
  tipoPedido: TipoPedido = TipoPedido.DELIVERY;
  nomeCliente = '';
  telefone = '';
  endereco = '';
  numeroMesa = '';
  numeroComanda = '';
  comentario = '';
  formaPagamento = '';
  valorPago: number | null = null;

  // Resumo colapsavel
  resumoExpandido = false;

  // Minhas informacoes colapsavel
  infoExpandido = true;

  // Opcoes de forma de pagamento
  formasPagamento: { label: string; value: string }[] = [];

  texts: any = {};

  private subs: Subscription[] = [];

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private freteService: CardapioFreteService,
  ) {}

  ngOnInit(): void {
    if (this.tipoPedidoInicial) {
      this.tipoPedido = this.tipoPedidoInicial;
    }
    if (this.nomeClienteInicial) {
      this.nomeCliente = this.nomeClienteInicial;
    }
    if (this.telefoneInicial) {
      this.telefone = this.telefoneInicial;
    }
    if (this.enderecoInicial) {
      this.endereco = this.enderecoInicial;
    }
    if (this.numeroMesaInicial) {
      this.numeroMesa = this.numeroMesaInicial;
    }
    if (this.numeroComandaInicial) {
      this.numeroComanda = this.numeroComandaInicial;
    }
    this.loadTranslations();
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.loadTranslations())
    );
  }

  get subtotal(): number {
    return this.itens.reduce((acc, item) => acc + (Number(item.valorTotal) || 0), 0);
  }

  get frete(): number {
    // Frete por distância (backend) tem prioridade no delivery — o preview passa
    // a exibir o mesmo valor que será cobrado ao criar o pedido.
    if (this.isDelivery && this.freteDistancia != null) {
      return this.freteDistancia;
    }
    return this.freteService.calcularFrete(this.subtotal, this.tipoPedido, this.freteConfig);
  }

  get total(): number {
    return this.subtotal + this.frete;
  }

  get isDelivery(): boolean {
    return this.tipoPedido === TipoPedido.DELIVERY;
  }

  get isMesa(): boolean {
    return this.tipoPedido === TipoPedido.MESA;
  }

  /** Retorna true quando forma de pagamento e Dinheiro */
  get isDinheiro(): boolean {
    return this.formaPagamento === 'DINHEIRO';
  }

  /** Calcula o troco: valorPago - total. Retorna null se valorPago nao preenchido */
  get trocoCalculado(): number | null {
    if (this.valorPago === null || this.valorPago === undefined) return null;
    return this.valorPago - this.total;
  }

  /** Status do troco para exibicao condicional no template */
  get trocoStatus(): 'positivo' | 'exato' | 'insuficiente' | null {
    const troco = this.trocoCalculado;
    if (troco === null) return null;
    if (troco > 0) return 'positivo';
    if (troco === 0) return 'exato';
    return 'insuficiente';
  }

  get totalItens(): number {
    return this.itens.reduce((acc, item) => acc + item.quantidade, 0);
  }

  /** Retorna o titulo do tipo de pedido para o header */
  get tituloTipoPedido(): string {
    switch (this.tipoPedido) {
      case TipoPedido.MESA:
        return this.texts.carrinho?.tipo_mesa || 'No local';
      case TipoPedido.RETIRADA:
        return this.texts.carrinho?.tipo_retirada || 'Retirada';
      case TipoPedido.DELIVERY:
        return this.texts.carrinho?.tipo_delivery || 'Delivery';
      default:
        return '';
    }
  }

  formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  toggleResumo(): void {
    this.resumoExpandido = !this.resumoExpandido;
  }

  toggleInfo(): void {
    this.infoExpandido = !this.infoExpandido;
  }

  /** Mascara o nome: mostra 3 primeiras letras + *** */
  get nomeMascarado(): string {
    if (!this.nomeCliente || this.nomeCliente.length <= 3) return this.nomeCliente;
    return this.nomeCliente.substring(0, 3) + '***';
  }

  /** Mascara o telefone: mostra primeiros digitos + *** */
  get telefoneMascarado(): string {
    if (!this.telefone || this.telefone.length <= 6) return this.telefone;
    return this.telefone.substring(0, this.telefone.length - 3) + '***';
  }

  /** Limpa o valor pago quando a forma de pagamento muda para algo diferente de Dinheiro */
  onFormaPagamentoChange(): void {
    if (!this.isDinheiro) {
      this.valorPago = null;
    }
  }

  /**
   * Seleciona a forma de pagamento via botao de opcao.
   *
   * QR Code e cartao sao cobrados pela Bancard: o codigo e pedido JA NA ESCOLHA, para o cliente
   * pagar sem sair desta etapa. As demais formas seguem como sempre — o pagamento e combinado
   * com a loja depois.
   */
  selecionarFormaPagamento(value: string): void {
    const anterior = this.formaPagamento;
    this.formaPagamento = value;
    this.onFormaPagamentoChange();

    if (!this.cobraPorQrCode) {
      // Trocou para dinheiro/transferencia: a cobranca em aberto nao vale mais.
      if (this.formaCobraPorQrCode(anterior)) {
        this.cancelarCobrancaQr.emit();
      }
      return;
    }

    // A loja recusaria o pedido sem mesa/comanda, e a cobranca usa o mesmo payload: melhor
    // avisar aqui do que gerar um QR que nunca vira pedido.
    if (this.faltaMesaOuComanda) return;

    this.gerarCobrancaQr.emit(this.montarForm());
  }

  /**
   * A forma escolhida e cobrada pela Bancard AQUI, no checkout.
   *
   * Sao duas cobrancas diferentes: QR Code gera o codigo que o cliente le no app do banco; cartao
   * gera a pagina do tpago, que ele abre para pagar.
   *
   * Depende da loja ter escolhido a conta de recebimento da forma. Sem isso a forma continua
   * selecionavel, mas nao gera nada: o pedido segue como os de dinheiro, com o pagamento
   * combinado com a loja depois.
   */
  get cobraPorQrCode(): boolean {
    return this.formaCobraPorQrCode(this.formaPagamento);
  }

  private formaCobraPorQrCode(forma: string): boolean {
    const config = this.formasDePagamentoConfig;
    if (forma === 'QR_CODE') return this.temCobrancaOnline(config?.cobrancaOnlineQrCode, config?.qrCode);
    if (forma === 'TARJETA') return this.temCobrancaOnline(config?.cobrancaOnlineTarjeta, config?.tarjeta);
    return false;
  }

  /**
   * Backend antigo nao manda `cobrancaOnline*`; nesse caso vale a flag da forma, que era o
   * comportamento anterior (toda forma de gateway gerava cobranca).
   */
  private temCobrancaOnline(cobrancaOnline?: boolean, formaHabilitada?: boolean): boolean {
    return cobrancaOnline === undefined ? formaHabilitada === true : cobrancaOnline === true;
  }

  /** A forma escolhida e cartao: em vez do QR, o cliente recebe o link do tpago. */
  get cobraPorCartao(): boolean {
    return this.formaPagamento === 'TARJETA' && this.cobraPorQrCode;
  }

  /** O QR ja foi gerado e esta na tela, esperando o cliente pagar. */
  get exibirQrCode(): boolean {
    return this.cobraPorQrCode && !this.cobraPorCartao && !!this.estadoCobrancaQr?.cobranca?.qrData;
  }

  /** O link de pagamento por cartao ja veio e o cliente pode abrir a pagina do tpago. */
  get exibirLinkDeCartao(): boolean {
    return this.cobraPorCartao && !!this.estadoCobrancaQr?.cobranca?.linkUrl;
  }

  /** Abre a pagina de pagamento do tpago em outra aba, para o checkout continuar aberto aqui. */
  abrirLinkDeCartao(): void {
    const link = this.estadoCobrancaQr?.cobranca?.linkUrl;
    if (!link) return;
    window.open(link, '_blank', 'noopener');
  }

  /**
   * O botao de confirmar fica bloqueado enquanto o pagamento por QR nao entra: e a Bancard que
   * confirma, e o pedido so nasce depois disso. Assim que confirma, o pai avanca sozinho.
   */
  get aguardandoPagamentoQr(): boolean {
    return this.cobraPorQrCode && !this.estadoCobrancaQr?.confirmado;
  }

  /**
   * O rotulo "Aguardando o pagamento" so faz sentido quando ha mesmo o que esperar. Com a
   * cobranca em erro, o botao segue bloqueado — pagar e o unico caminho —, mas dizer que se
   * espera um pagamento que nunca vai chegar so confunde.
   */
  get exibirRotuloAguardandoPagamento(): boolean {
    return this.aguardandoPagamentoQr && !this.estadoCobrancaQr?.erro;
  }

  /** Tenta gerar o QR de novo depois de um erro (loja sem Bancard, rede, etc). */
  tentarGerarQrNovamente(): void {
    if (!this.cobraPorQrCode || this.faltaMesaOuComanda) return;
    this.gerarCobrancaQr.emit(this.montarForm());
  }

  /** Copia o payload EMV para o cliente colar no app do banco. */
  copiarCodigoQr(): void {
    const codigo = this.estadoCobrancaQr?.cobranca?.qrData;
    if (!codigo) return;
    navigator.clipboard?.writeText(codigo).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.texts.checkout?.qr_codigo_copiado || 'Codigo copiado',
        life: 2000,
      });
    });
  }

  /**
   * Traz a cobranca para o centro da tela assim que ela chega — o QR ou o botao de pagar com
   * cartao —, e de novo quando o pagamento e confirmado.
   *
   * Quem rola e o `.checkout-body`, nao a pagina: `scrollIntoView` cuida disso sozinho, porque
   * age em todos os containers rolaveis acima do elemento. O `setTimeout` espera o *ngIf
   * renderizar o bloco — sem ele o ViewChild ainda nao existe.
   */
  private rolarAteACobranca(mudanca: SimpleChange): void {
    const anterior = mudanca.previousValue as CardapioEstadoCobrancaQrDTO | undefined;
    const atual = mudanca.currentValue as CardapioEstadoCobrancaQrDTO | undefined;

    const tinhaCobranca = !!(anterior?.cobranca?.qrData || anterior?.cobranca?.linkUrl);
    const temCobranca = !!(atual?.cobranca?.qrData || atual?.cobranca?.linkUrl);
    const acabouDeAparecer = temCobranca && !tinhaCobranca;
    const acabouDeConfirmar = temCobranca && atual?.confirmado === true && anterior?.confirmado !== true;

    if (!acabouDeAparecer && !acabouDeConfirmar) return;

    setTimeout(() => {
      this.blocoCobrancaQr?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['estadoCobrancaQr']) {
      this.rolarAteACobranca(changes['estadoCobrancaQr']);
    }
    if (changes['visible'] && this.visible) {
      // Resetar estado ao abrir o dialog
      this.resetFormState();
    }
    // A configuracao do cardapio chega junto/depois do primeiro render: remonta
    // as opcoes quando ela mudar, senao o checkout ficaria no fallback historico.
    if (changes['formasDePagamentoConfig']) {
      this.montarFormasPagamento();
    }
  }

  /** Ao exibir o dialog */
  onDialogShow(): void {
    this.resetFormState();
  }

  /** Ao esconder o dialog, emite eventos */
  onDialogHide(): void {
    this.visibleChange.emit(false);
    this.voltar.emit();
  }

  onVoltar(): void {
    this.onDialogHide();
  }

  private resetFormState(): void {
    if (this.tipoPedidoInicial) {
      this.tipoPedido = this.tipoPedidoInicial;
    }
    if (this.nomeClienteInicial) {
      this.nomeCliente = this.nomeClienteInicial;
    }
    if (this.telefoneInicial) {
      this.telefone = this.telefoneInicial;
    }
    if (this.enderecoInicial) {
      this.endereco = this.enderecoInicial;
    }
    // Pre-preencher mesa e comanda (coletadas no modal de identificacao do carrinho)
    this.numeroMesa = this.numeroMesaInicial || '';
    this.numeroComanda = this.numeroComandaInicial || '';
    this.resumoExpandido = false;
    this.infoExpandido = true;
    this.comentario = '';
    this.formaPagamento = '';
    this.valorPago = null;
  }

  /** A loja exige a mesa neste pedido de consumo no local. */
  get exigeNumeroDaMesa(): boolean {
    return this.isMesa && this.exigenciasDoPedido?.numeroDaMesa === true;
  }

  /** A loja exige a comanda neste pedido de consumo no local. */
  get exigeNumeroDaComanda(): boolean {
    return this.isMesa && this.exigenciasDoPedido?.numeroDaComanda === true;
  }

  /**
   * Falta algo que a loja exige. O botão de finalizar usa isto: barrar aqui evita o cliente
   * mandar um pedido que o backend vai recusar.
   */
  get faltaMesaOuComanda(): boolean {
    if (this.exigeNumeroDaMesa && !this.numeroMesa.trim()) return true;
    if (this.exigeNumeroDaComanda && !this.numeroComanda.trim()) return true;
    return false;
  }

  onFinalizar(): void {
    if (this.faltaMesaOuComanda) return;
    // Pagamento por QR quem conclui e a confirmacao da Bancard, nao o clique.
    if (this.aguardandoPagamentoQr) return;

    this.pedidoFinalizado.emit(this.montarForm());
  }

  /** Monta o que o backend recebe — do pedido e, com o mesmo formato, da cobranca por QR. */
  private montarForm(): CardapioCheckoutFormDTO {
    // Buscar o label traduzido da forma de pagamento selecionada
    const formaPagamentoSelecionada = this.formasPagamento.find(f => f.value === this.formaPagamento);
    const formaPagamentoLabel = formaPagamentoSelecionada?.label || this.formaPagamento;

    const form: CardapioCheckoutFormDTO = {
      tipoPedido: this.tipoPedido,
      nomeCliente: this.nomeCliente.trim(),
      telefone: this.telefone.trim(),
      endereco: this.endereco.trim(),
      numeroMesa: this.numeroMesa.trim(),
      numeroComanda: this.numeroComanda.trim(),
      comentario: this.comentario.trim(),
      formaPagamento: formaPagamentoLabel,
      // O rotulo acima e so para exibir; o backend precisa do valor do enum.
      formaPagamentoValor: this.formaPagamento || undefined,
      ruc: this.rucInicial || undefined,
      ci: this.ciInicial || undefined,
    };

    // Incluir valorPago e troco se pagamento em dinheiro e valor preenchido
    if (this.isDinheiro && this.valorPago !== null && this.valorPago > 0) {
      form.valorPago = this.valorPago;
      const troco = this.trocoCalculado;
      if (troco !== null && troco >= 0) {
        form.troco = troco;
      }
    }

    return form;
  }

  trackByItem(index: number, item: CardapioItemPedidoDTO): string {
    return `${item.produto.id}-${JSON.stringify(item.opcoesSelecionadas)}`;
  }

  private loadTranslations(): void {
    this.texts = this.translate.instant('cardapio_digital');
    this.montarFormasPagamento();
  }

  /**
   * Monta as formas de pagamento conforme o que o lojista habilitou.
   *
   * Enquanto a loja nao ligar "perguntar a forma de pagamento", o checkout nao
   * mostra a secao — nao pergunta nada e nao envia forma nenhuma.
   */
  private montarFormasPagamento(): void {
    const config = this.formasDePagamentoConfig;

    // Loja nao pergunta a forma de pagamento: a secao inteira some do checkout.
    if (!config?.exibir) {
      this.formasPagamento = [];
      this.formaPagamento = '';
      return;
    }

    const catalogo = [
      { habilitada: config.dinheiro, label: this.texts.checkout?.pagamento_dinheiro || 'Dinheiro', value: 'DINHEIRO' },
      { habilitada: config.tarjeta, label: this.texts.checkout?.pagamento_cartao || 'Cartao', value: 'TARJETA' },
      { habilitada: config.qrCode, label: this.texts.checkout?.pagamento_qrcode || 'QR Code', value: 'QR_CODE' },
      { habilitada: config.transferencia, label: this.texts.checkout?.pagamento_transferencia || 'Transferencia', value: 'TRANSFERENCIA' },
    ];

    this.formasPagamento = catalogo
      .filter(opcao => opcao.habilitada)
      .map(({ label, value }) => ({ label, value }));

    // A forma escolhida pode nao existir mais: melhor limpar do que enviar uma
    // forma que o backend vai recusar.
    if (this.formaPagamento && !this.formasPagamento.some(f => f.value === this.formaPagamento)) {
      this.formaPagamento = '';
    }
  }

  /** A loja pergunta a forma de pagamento no checkout. */
  get exibirFormasPagamento(): boolean {
    return this.formasPagamento.length > 0;
  }

  /**
   * O cliente escolheu transferencia. A tela so avisa que a loja passa os dados: os dados
   * bancarios da empresa nao vao para o cardapio publico.
   */
  get isTransferencia(): boolean {
    return this.formaPagamento === 'TRANSFERENCIA';
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
